/* LicensedTime core: team data, finder, mega menu, drawer, My team. Vanilla JS, no dependencies. */
(() => {
  'use strict';

  const dataEl = document.getElementById('lt-data');
  if (!dataEl) return;
  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }

  const LS_MY = 'lt_my_team';
  const LS_RECENT = 'lt_recent_teams';
  const CONF_ORDER = ['SEC', 'Big Ten', 'ACC', 'Big 12', 'American', 'Independent'];
  const CONF_LABEL = { American: 'American · Ind.', Independent: 'American · Ind.' };
  const CONF_LABEL_MOBILE = { American: 'American & Independent', Independent: 'American & Independent' };

  const boot = (rawTeams) => {
  // One status rule for every menu and finder: a team is linked only when the endpoint marks it live
  // (status = live, has a collection, and the viewer may see it). Everything else is gray and unlinked.
  const teams = rawTeams.map((t) => ({ ...t, a: Array.isArray(t.a) ? t.a : [], live: t.live === true && !!t.u }));
  const byHandle = new Map(teams.map((t) => [t.h, t]));

  /* ---------- helpers ---------- */
  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const validHex = (h) => (typeof h === 'string' && /^#[0-9a-f]{3,8}$/i.test(h) ? h : '');
  const dot = (t, extra) => {
    const hex = validHex(t.p);
    return `<span class="lt-dot${t.live ? '' : ' lt-dot--soon'}${extra ? ' ' + extra : ''}"${hex && t.live ? ` style="background:${hex}"` : ''} aria-hidden="true"></span>`;
  };
  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));
  const storeGet = (k) => {
    try {
      return window.localStorage.getItem(k);
    } catch (e) {
      return null;
    }
  };
  const storeSet = (k, v) => {
    try {
      window.localStorage.setItem(k, v);
    } catch (e) {
      /* storage can be blocked; the page still works without it */
    }
  };
  const compare = (a, b) => (a.o || 0) - (b.o || 0) || a.s.localeCompare(b.s);

  /* ---------- My team and recently viewed ---------- */
  const myTeam = () => {
    const h = storeGet(LS_MY) || data.serverMyTeam || '';
    return byHandle.get(h) || null;
  };
  const recent = () => {
    try {
      return JSON.parse(storeGet(LS_RECENT) || '[]').filter((h) => byHandle.has(h));
    } catch (e) {
      return [];
    }
  };
  const emit = () => document.dispatchEvent(new CustomEvent('lt:myteam', { detail: { team: myTeam() } }));
  const setMyTeam = (handle) => {
    if (!byHandle.has(handle)) return;
    storeSet(LS_MY, handle);
    emit();
  };
  const clearMyTeam = () => {
    try {
      window.localStorage.removeItem(LS_MY);
    } catch (e) {
      /* storage blocked */
    }
    data.serverMyTeam = '';
    emit();
  };
  if (data.pageIsHub && data.pageTeam && byHandle.has(data.pageTeam)) {
    // Visiting a team hub records it as recently viewed; it never changes My team.
    const list = recent().filter((h) => h !== data.pageTeam);
    list.unshift(data.pageTeam);
    storeSet(LS_RECENT, JSON.stringify(list.slice(0, 5)));
  }

  /* ---------- leagues and conference grouping ---------- */
  const leagueTeams = (code) => teams.filter((t) => t.lg === code);
  const hasLive = (code) => leagueTeams(code).some((t) => t.live);
  let leagues = data.leagues.slice().sort((a, b) => a.sort - b.sort).filter((l) => leagueTeams(l.code).length);
  const liveLeagues = leagues.filter((l) => hasLive(l.code));
  if (liveLeagues.length) leagues = liveLeagues; // menu lists only leagues with live teams
  let currentLeague = (myTeam() && leagues.some((l) => l.code === myTeam().lg) ? myTeam().lg : leagues[0] && leagues[0].code) || '';

  const groupByConference = (code, labels) => {
    const map = new Map();
    leagueTeams(code).forEach((t) => {
      const key = t.cf || 'All teams';
      const label = (labels && labels[key]) || key;
      if (!map.has(label)) map.set(label, { label, rank: CONF_ORDER.indexOf(key) === -1 ? 99 : CONF_ORDER.indexOf(key), teams: [] });
      map.get(label).teams.push(t);
    });
    return Array.from(map.values())
      .sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label))
      .map((g) => ({ ...g, teams: g.teams.sort(compare) }));
  };

  /* ---------- team markup ---------- */
  const teamItem = (t, opts) => {
    const mine = myTeam() && myTeam().h === t.h ? ' is-mine' : '';
    const label = esc(t.s);
    if (t.live && t.u) return `<a class="lt-team${mine}" href="${esc(t.u)}">${dot(t)}${label}</a>`;
    return `<span class="lt-team is-soon">${dot(t)}${label}${opts && opts.soonTag ? ' <small>· Soon</small>' : ''}</span>`;
  };

  const resultItem = (t) => {
    const sub = `${esc(t.lg)}${t.cf ? ' · ' + esc(t.cf) : ''}${t.live ? '' : ' · Coming soon'}`;
    const inner = `${dot(t, 'lt-dot--lg')}<span class="lt-result__main"><span class="lt-result__name">${esc(t.n)}</span><span class="lt-result__sub">${sub}</span></span>`;
    if (t.live && t.u) return `<a class="lt-result" href="${esc(t.u)}" data-lt-pick="${esc(t.h)}">${inner}</a>`;
    return `<div class="lt-result is-soon" aria-disabled="true">${inner}</div>`;
  };

  /* ---------- finder ---------- */
  const matches = (q) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return teams
      .filter((t) => [t.n, t.s, t.m, t.c].concat(t.a).some((v) => v && String(v).toLowerCase().includes(needle)))
      .sort((a, b) => Number(b.live) - Number(a.live) || a.n.localeCompare(b.n))
      .slice(0, 8);
  };

  const initFinder = (label, onState) => {
    const input = $('.lt-finder__input', label);
    const host = label.closest('[data-lt-finder-host]') || label.parentElement;
    const results = $('[data-lt-finder-results]', host);
    if (!input || !results) return;
    const render = () => {
      const q = input.value;
      const found = matches(q);
      const active = q.trim().length > 0;
      results.hidden = !active;
      if (active) {
        results.innerHTML = found.length
          ? `<div class="lt-result-label">${found.length} team${found.length > 1 ? 's' : ''} match</div><div class="lt-results">${found.map(resultItem).join('')}</div>`
          : '<div class="lt-result-label">No teams match. Try a nickname or city.</div>';
      } else {
        results.innerHTML = '';
      }
      if (onState) onState(active);
    };
    input.addEventListener('input', render);
    // Choosing a team in the finder is a deliberate act: it becomes My team.
    results.addEventListener('click', (e) => {
      const a = e.target.closest('[data-lt-pick]');
      if (a) setMyTeam(a.getAttribute('data-lt-pick'));
    });
    label._ltReset = () => {
      input.value = '';
      render();
    };
  };
  window.LT = { teams, byHandle, leagues, groupByConference, getMyTeam: myTeam, setMyTeam, clearMyTeam, recent, initFinder, matches };

  /* ---------- counts for the Your team panel ---------- */
  const countCache = new Map();
  const getCounts = (t) => {
    if (!t.u) return Promise.resolve(null);
    if (!countCache.has(t.h)) {
      countCache.set(
        t.h,
        (async () => {
          let page = 1;
          let pages = 1;
          const total = { watches: 0, clocks: 0, tech: 0 };
          do {
            const r = await fetch(`${t.u}?view=lt-counts&page=${page}`);
            const j = JSON.parse(await r.text());
            total.watches += j.watches;
            total.clocks += j.clocks;
            total.tech += j.tech;
            pages = j.pages || 1;
            page += 1;
          } while (page <= pages);
          return total;
        })().catch(() => null)
      );
    }
    return countCache.get(t.h);
  };

  /* ---------- header: mega menu, chip, drawer ---------- */
  const header = $('[data-lt-header]');
  if (!header) return;
  const mega = $('[data-lt-mega]');
  const megaToggle = $('[data-lt-mega-toggle]');
  const leaguesCol = $('[data-lt-leagues]');
  const grid = $('[data-lt-team-grid]');
  const legend = $('[data-lt-legend]');
  const panel = $('[data-lt-panel]');
  const megaResults = mega ? $('.lt-mega__results', mega) : null;
  let chip = $('[data-lt-chip]');

  const renderLeagues = () => {
    if (!leaguesCol) return;
    leaguesCol.innerHTML =
      '<div class="lt-eyebrow">Leagues</div>' +
      leagues
        .map(
          (l) =>
            `<button type="button" class="lt-league-btn" data-lt-league="${esc(l.code)}" aria-pressed="${l.code === currentLeague}"><span>${esc(l.name)}</span><span>${leagueTeams(l.code).length}</span></button>`
        )
        .join('');
  };
  const renderGrid = () => {
    if (!grid) return;
    const groups = groupByConference(currentLeague, CONF_LABEL);
    grid.innerHTML = groups
      .map((g) => `<div class="lt-conf"><div class="lt-conf__name">${esc(g.label)}</div><div class="lt-conf__list">${g.teams.map((t) => teamItem(t)).join('')}</div></div>`)
      .join('');
    if (legend) legend.hidden = !teams.some((t) => t.lg === currentLeague && !t.live);
  };

  const renderPanel = () => {
    if (!panel) return;
    const my = myTeam();
    const last = !my && recent().length ? byHandle.get(recent()[0]) : null;
    const t = my || last;
    if (!t) {
      panel.innerHTML = '<div class="lt-eyebrow">Your team</div><p class="lt-panel__note">Find your team to see it here.</p>';
      return;
    }
    const hex = validHex(t.p) || '#16181b';
    const tile = `<a class="lt-tile" ${t.live && t.u ? `href="${esc(t.u)}"` : 'aria-disabled="true"'} style="--tile-bg:${hex};--tile-rule:${validHex(t.op) ? '#fff' : '#fff'}">${t.img ? `<img class="lt-tile__img" src="${esc(t.img)}" alt="" loading="lazy" width="280" height="170">` : ''}<span class="lt-tile__text"><span class="lt-tile__rule"></span><span class="lt-tile__name">${esc(t.s)}</span><span class="lt-tile__sub">${esc(t.m)}${t.cf ? ' · ' + esc(t.cf) : ''}</span></span></a>`;
    if (last) {
      panel.innerHTML = `<div class="lt-eyebrow">Last viewed</div>${tile}<button type="button" class="lt-linkbtn" data-lt-make-mine="${esc(t.h)}">Make ${esc(t.s)} my team</button>`;
      return;
    }
    panel.innerHTML = `<div class="lt-eyebrow">Your team</div>${tile}<div class="lt-panel__links" data-lt-counts></div><button type="button" class="lt-linkbtn" data-lt-change>Change my team</button>`;
    if (t.live) {
      getCounts(t).then((c) => {
        const box = $('[data-lt-counts]', panel);
        if (!c || !box || !myTeam() || myTeam().h !== t.h) return;
        box.innerHTML = [['watches', 'watches'], ['clocks', 'clocks'], ['tech', 'tech']]
          .filter(([k]) => c[k] > 0)
          .map(([k, w]) => `<a href="${esc(t.u)}#${k}">${esc(t.s)} ${w} <span>${c[k]}</span></a>`)
          .join('');
      });
    }
  };

  const renderChip = () => {
    const my = myTeam();
    const el = my && my.live && my.u ? document.createElement('a') : document.createElement('button');
    el.className = 'lt-chip';
    el.setAttribute('data-lt-chip', '');
    if (el.tagName === 'A') {
      el.href = my.u;
    } else {
      el.type = 'button';
      el.setAttribute('aria-controls', 'lt-mega');
      el.setAttribute('data-lt-open-mega', '');
    }
    el.innerHTML = my ? `${dot(my)}My team: ${esc(my.s)}` : 'Pick your team';
    if (chip) chip.replaceWith(el);
    chip = el;
  };

  const renderDrawer = () => {
    const pills = $('[data-lt-drawer-leagues]');
    const confs = $('[data-lt-drawer-confs]');
    const myLink = $('[data-lt-drawer-myteam]');
    if (pills) {
      pills.innerHTML = leagues
        .map((l) => `<button type="button" class="lt-pill" data-lt-drawer-league="${esc(l.code)}" aria-pressed="${l.code === currentLeague}">${esc(l.name)}</button>`)
        .join('');
    }
    if (confs) {
      const open = confs.dataset.open || (groupByConference(currentLeague, CONF_LABEL_MOBILE)[0] || {}).label;
      confs.innerHTML = groupByConference(currentLeague, CONF_LABEL_MOBILE)
        .map((g, i) => {
          const isOpen = g.label === open;
          return `<div class="lt-conf-acc"><button type="button" class="lt-conf-acc__btn" aria-expanded="${isOpen}" aria-controls="lt-conf-${i}" data-lt-conf="${esc(g.label)}"><span>${esc(g.label)} <em>${g.teams.length}</em></span><svg width="14" height="14" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M4 7l5 5 5-5"></path></svg></button><div class="lt-conf-acc__list" id="lt-conf-${i}"${isOpen ? '' : ' hidden'}>${g.teams.map((t) => teamItem(t, { soonTag: true })).join('')}</div></div>`;
        })
        .join('');
    }
    if (myLink) {
      const my = myTeam();
      const arrow = '<svg width="14" height="14" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M4.5 3l3 3-3 3"></path></svg>';
      if (my && my.live && my.u) {
        myLink.innerHTML = `<a class="lt-drawer__myteam" href="${esc(my.u)}" style="--lt-my-bg:${validHex(my.p) || '#16181b'};--lt-my-fg:${validHex(my.op) || '#ffffff'}">${dot(my)}<span><small>My team</small><strong>Shop ${esc(my.s)}</strong></span>${arrow}</a>`;
      } else if (my) {
        myLink.innerHTML = `<div class="lt-drawer__myteam is-soon">${dot(my)}<span><small>My team</small><strong>${esc(my.s)} · Coming soon</strong></span></div>`;
      } else {
        myLink.innerHTML = `<button type="button" class="lt-drawer__myteam lt-drawer__pick" data-lt-drawer-pick><span class="lt-dot lt-dot--soon lt-dot--lg" aria-hidden="true"></span><span><small>My team</small><strong>Pick your team</strong></span>${arrow}</button>`;
      }
    }
  };

  const renderAll = () => {
    renderLeagues();
    renderGrid();
    renderPanel();
    renderChip();
    renderDrawer();
  };
  renderAll();
  document.addEventListener('lt:myteam', renderAll);
  window.addEventListener('storage', (e) => {
    if (e.key === LS_MY || e.key === LS_RECENT) renderAll();
  });

  /* mega menu open/close */
  const setMega = (open, focusInput) => {
    if (!mega || !megaToggle) return;
    mega.hidden = !open;
    megaToggle.setAttribute('aria-expanded', String(open));
    if (open && focusInput) {
      const input = $('.lt-finder__input', mega);
      if (input) input.focus();
    }
  };
  if (mega) {
    $$('[data-lt-finder]', mega).forEach((label) =>
      initFinder(label, (active) => {
        if (grid) grid.hidden = active;
        if (legend && active) legend.hidden = true;
        else if (legend) renderGrid();
      })
    );
    header.addEventListener('click', (e) => {
      if (e.target.closest('[data-lt-mega-toggle]')) {
        setMega(mega.hidden, false);
        return;
      }
      if (e.target.closest('[data-lt-open-mega]')) {
        setMega(true, true);
        return;
      }
      const lg = e.target.closest('[data-lt-league]');
      if (lg) {
        currentLeague = lg.getAttribute('data-lt-league');
        renderLeagues();
        renderGrid();
        return;
      }
      const mk = e.target.closest('[data-lt-make-mine]');
      if (mk) {
        setMyTeam(mk.getAttribute('data-lt-make-mine'));
        return;
      }
      if (e.target.closest('[data-lt-change]')) {
        const input = $('.lt-finder__input', mega);
        if (input) {
          input.value = '';
          input.dispatchEvent(new Event('input'));
          input.focus();
        }
      }
    });
    document.addEventListener('click', (e) => {
      if (!mega.hidden && !header.contains(e.target)) setMega(false);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !mega.hidden) {
        setMega(false);
        megaToggle.focus();
      }
    });
    mega.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!mega.hidden && !header.contains(document.activeElement) && document.activeElement !== document.body) setMega(false);
      }, 0);
    });
  }

  /* drawer */
  const drawer = $('[data-lt-drawer]');
  if (drawer) {
    $$('[data-lt-finder]', drawer).forEach((label) => initFinder(label));
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-lt-drawer-open]')) {
        if (typeof drawer.showModal === 'function' && !drawer.open) drawer.showModal();
        drawer.focus({ preventScroll: true });
        return;
      }
      if (e.target.closest('[data-lt-drawer-close]') || e.target === drawer) {
        drawer.close();
        return;
      }
      if (!drawer.contains(e.target)) return;
      if (e.target.closest('[data-lt-drawer-pick]')) {
        const input = $('.lt-finder__input', drawer);
        if (input) input.focus();
        return;
      }
      const lg = e.target.closest('[data-lt-drawer-league]');
      if (lg) {
        currentLeague = lg.getAttribute('data-lt-drawer-league');
        $('[data-lt-drawer-confs]', drawer).dataset.open = '';
        renderDrawer();
        renderLeagues();
        renderGrid();
        return;
      }
      const cf = e.target.closest('[data-lt-conf]');
      if (cf) {
        const holder = $('[data-lt-drawer-confs]', drawer);
        const wasOpen = cf.getAttribute('aria-expanded') === 'true';
        holder.dataset.open = wasOpen ? '__none__' : cf.getAttribute('data-lt-conf');
        renderDrawer();
        return;
      }
      const acc = e.target.closest('[data-lt-acc]');
      if (acc) {
        const open = acc.getAttribute('aria-expanded') === 'true';
        acc.setAttribute('aria-expanded', String(!open));
        $('#' + acc.getAttribute('aria-controls')).hidden = open;
      }
    });
    const mq = window.matchMedia('(min-width: 1024px)');
    const closeOnDesktop = () => {
      if (mq.matches && drawer.open) drawer.close();
    };
    if (mq.addEventListener) mq.addEventListener('change', closeOnDesktop);
  }

  /* keep Horizon's sticky offsets in step with the LicensedTime header */
  const group = $('#header-group');
  const setHeights = () => {
    const h = Math.round(header.getBoundingClientRect().height);
    let all = 0;
    if (group) Array.from(group.querySelectorAll('.lt-promo, .lt-utility, .lt-header')).forEach((el) => (all += el.offsetHeight));
    document.body.style.setProperty('--header-height', `${h}px`);
    document.body.style.setProperty('--header-group-height', `${all}px`);
  };
  setHeights();
  if ('ResizeObserver' in window) new ResizeObserver(setHeights).observe(header);
  window.addEventListener('resize', setHeights);
  document.dispatchEvent(new CustomEvent('lt:ready'));
  };

  // A page restored from the back/forward cache could show old team status; reload it so status is current.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) window.location.reload();
  });

  // Team list comes from its own endpoint (paginated server side, gate-aware).
  fetch('/collections/all?view=lt-teams', { cache: 'no-store' })
    .then((r) => r.text())
    .then((txt) => boot(JSON.parse(txt).teams))
    .catch(() => boot([]));
})();
